import React, {createContext, useContext, useState, ReactNode, useRef, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {
    Box,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useMultiStyleConfig,
    FlexProps,
    TextProps,
    StackProps,
    VStack,
    BoxProps,
    VisuallyHidden,
} from '@chakra-ui/react'
import {IconButton, Icon, Picture} from '_uiComponents'
import type {IconNameTypes} from '_types'

interface ModalOptions {
    imageUrl?: string
    imageAlt?: string
    title?: string | ReactNode
    body: ReactNode
    footer?: ReactNode
    variant?: string
    closingIcon?: IconNameTypes
    closingIconSize?: 'sm' | 'md' | 'lg'
    showCloseButton?: boolean
    onClose?: () => void
    className?: string
}

interface ModalContextType {
    openModal: (options: ModalOptions) => void
    closeModal: () => void
    updateModalTitle: (title: string | ReactNode) => void
    updateModalBody: (body: ReactNode) => void
    updateModalFooter: (footer: ReactNode) => void
    updateModalImage: (url?: string, alt?: string) => void
    updateOnCloseCallback: (onCloseCallback: {fn: () => void}) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const ModalProvider = ({children}: {children: ReactNode}) => {
    const {formatMessage} = useIntl()
    const [variant, setVariant] = useState<string>()
    const [isOpen, setIsOpen] = useState(false)
    const [modalImage, setModalImage] = useState<{url?: string; alt?: string}>()
    const [modalTitle, setModalTitle] = useState<string | ReactNode | undefined>()
    const [modalBody, setModalBody] = useState<ReactNode | null>(null)
    const [modalFooter, setModalFooter] = useState<ReactNode | null>(null)
    const [closingIcon, setClosingIcon] = useState<IconNameTypes>('IconClose')
    const [closingIconSize, setClosingIconSize] = useState<'sm' | 'md' | 'lg'>('md')
    const [showCloseButton, setShowCloseButton] = useState(true)
    const [onCloseCallback, setOnCloseCallback] = useState<{fn: () => void} | null>(null)
    const [className, setClassName] = useState<string>('')

    const styles = useMultiStyleConfig('Modal', {variant}) as Record<string, FlexProps & TextProps & StackProps>

    const modalContentRef = useRef<HTMLDivElement>(null)
    const isTitleVisuallyHidden = variant === 'changeLocale' || variant === 'switchLocale'
    const modalTitleId = 'modal-title'

    const openModal = ({
        imageUrl,
        imageAlt,
        title,
        body,
        footer,
        variant,
        closingIcon = 'IconClose',
        closingIconSize = 'md',
        showCloseButton = true,
        onClose,
        className,
    }: ModalOptions) => {
        setModalImage({url: imageUrl, alt: imageAlt})
        setModalTitle(title)
        setModalBody(body)
        setModalFooter(footer)
        setOnCloseCallback({fn: () => onClose?.()})
        setVariant(variant)
        setIsOpen(true)
        setClosingIcon(closingIcon)
        setClosingIconSize(closingIconSize)
        setClassName(className || '')
        setShowCloseButton(showCloseButton)
    }

    const closeModal = () => {
        onCloseCallback?.fn?.()
        setIsOpen(false)
    }

    const updateModalTitle = (title: string | ReactNode) => {
        setModalTitle(title)
    }

    const updateModalBody = (body: ReactNode) => {
        setModalBody(body)
    }

    const updateModalFooter = (footer: ReactNode) => {
        setModalFooter(footer)
    }

    const updateModalImage = (url?: string, alt?: string) => {
        if (modalImage?.url !== url || modalImage?.alt !== alt) {
            setModalImage({url, alt})
        }
    }

    const updateOnCloseCallback = (onCloseCallback: {fn: () => void}) => {
        setOnCloseCallback(onCloseCallback)
    }

    /**
     * Since Chakra does not support setup of `aria-labelledby` parameter on modal content
     * this setup on opening ensures that it is configured correctly pointing to the text that
     * actually contains the title label of the Modal.
     */
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (modalContentRef.current) {
                    modalContentRef.current.setAttribute('aria-labelledby', modalTitleId)
                }
            }, 500)
        }
    }, [isOpen])

    return (
        <ModalContext.Provider value={{openModal, closeModal, updateModalTitle, updateModalBody, updateModalFooter, updateModalImage, updateOnCloseCallback}}>
            {children}
            <Modal isOpen={isOpen} onClose={closeModal} variant={variant}>
                <ModalOverlay />
                <ModalContent ref={modalContentRef} className={className}>
                    {(modalImage?.url || variant === 'NewsletterModal') && (
                        <Box {...(styles.imageContainer as BoxProps)}>
                            <Picture objectFit="cover" imagexs={modalImage?.url} alt={modalImage?.alt} />
                        </Box>
                    )}

                    <VStack className={'hello1'} {...styles.dialogWrapper}>
                        <VStack className={'hello2'} {...styles.bodyWrapper}>
                            <ModalHeader>
                                {modalTitle && (
                                    // A string title renders as a semantic <p> (Chakra Text). A ReactNode title
                                    // (e.g. the zoom modal header, which contains a VStack/<div>) must NOT be
                                    // wrapped in a <p>, otherwise React warns about invalid DOM nesting
                                    // (<div> cannot be a descendant of <p>). In that case use a <div> (Box).
                                    <Text
                                        id={modalTitleId}
                                        {...styles.headerTitle}
                                        as={isTitleVisuallyHidden ? VisuallyHidden : typeof modalTitle === 'string' ? Text : Box}
                                    >
                                        {modalTitle}
                                    </Text>
                                )}
                                {showCloseButton && (
                                    <Box {...styles.closeIcon}>
                                        <IconButton
                                            variant={'iconButtonBgTransparent'}
                                            size="iconButtonSm"
                                            icon={<Icon name={closingIcon} size={closingIconSize} />}
                                            onClick={closeModal}
                                            aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})}
                                        />
                                    </Box>
                                )}
                            </ModalHeader>
                            <ModalBody>{modalBody}</ModalBody>
                        </VStack>

                        {modalFooter && <ModalFooter>{modalFooter}</ModalFooter>}
                    </VStack>
                </ModalContent>
            </Modal>
        </ModalContext.Provider>
    )
}

export const useModal = (): ModalContextType => {
    const context = useContext(ModalContext)
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider')
    }
    return context
}
